import * as React from 'react'
import { Alert, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, InputAdornment, Radio, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { apiConstants, get, post, postGeneric } from "../../common/constants";
import { FormProvider } from "../../common/hook-form";
import { useState } from 'react';
import { Box } from '@mui/system';
import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { LoadingButton } from '@mui/lab';
import PageTitle from '../../components/PageTitle';
import { useGetAllActiveMerchantsIdAndNameNONKYCCachedQuery } from '../../redux/commonApiCalls';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import { formatCurrency } from '../../helpers/UtilityHelper';
import WalletSwitchHistory from './WalletSwitchHistory';

// extracts a readable message out of a GenericAPIResponse error payload
const apiErrorMessage = (resData, fallback) => {
    const ex = resData?.exception;
    if (!ex) return fallback;
    return typeof ex === 'string' ? ex : (ex.message || fallback);
};

function Wallet() {
    const [wallet, setwallet] = useState("");
    const [credit, setCredit] = useState();
    const [Threshold, setThreshold] = useState();
    const [balance, setBalance] = useState();
    const [amount, setAmount] = useState();
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = useState(false);
    const [reload, setReload] = useState(0)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('');
    // all wallet documents of the selected merchant (active + inactive), for the side-by-side
    // view and to preserve alertEmails on save (the old form overwrote them with [""])
    const [walletsData, setWalletsData] = useState([]);
    // pre-switch check: recharges initiated but not yet captured — captured money lands on
    // whichever wallet is ACTIVE at capture time, so switching with one pending is risky
    const [pendingRecharges, setPendingRecharges] = useState(null);

    const methods = useForm(
        {
            defaultValues: {
                MerchantName: null
            }
        }
    );
    const {
        handleSubmit,
        watch
    } = methods;

    const onSubmit = () => {
        if (error) {
            enqueueSnackbar(error, { variant: 'error' });
            return;
        }
        // a Postpaid wallet with no credit limit would reject every debit the moment it goes
        // live (backend also refuses it) — stop it here with a clear message
        if (wallet === 'Postpaid' && !(parseFloat(credit) > 0)) {
            enqueueSnackbar('Credit Limit is required (non-zero) before enabling a Postpaid wallet', { variant: 'error' });
            return;
        }
        if (!wallet) {
            enqueueSnackbar('Select a wallet type', { variant: 'error' });
            return;
        }
        handleClickOpen();
    }

    const handleChangeWallet = (e) => {
        const type = e.target.value;
        // hydrate the fields from THAT type's wallet doc so stale values from the other
        // wallet are never echoed into the save request
        const doc = walletsData.find((w) => w.walletType === type);
        setwallet(type);
        setThreshold(doc?.alertThreshold ?? '');
        setError('');
        if (type === 'Prepaid') {
            setBalance(doc?.walletBalance);
        } else {
            setAmount(doc?.dueAmount);
            setCredit(doc?.creditLimit ?? '');
            if (doc?.alertThreshold < 0.1 * doc?.creditLimit) {
                setError('Alert Threshold cannot be less than 10% of the Credit Limit');
            }
        }
    };

    const handleThreshold = (event) => {
        const value = parseFloat(event.target.value);
        setThreshold(value);
        if (wallet === 'Postpaid' && value < 0.1 * credit) {
            setError('Alert Threshold cannot be less than 10% of the Credit Limit');
        }
        else if (wallet === 'Postpaid' && value > credit) {
            setError('Alert threshold can not be greater than credit limit');
        }
        else {
            setError('');
        }
    };

    const handleCredit = (event) => {
        const value = parseFloat(event.target.value);
        setCredit(value);
        if (wallet === 'Postpaid' && Threshold < 0.1 * value) {
            setError('Alert Threshold cannot be less than 10% of the Credit Limit');
        }
        else if (wallet === 'Postpaid' && Threshold > value) {
            setError('Alert threshold can not be greater than credit limit');
        }
        else {
            setError('');
        }
    };

    const merchant = watch("merchant")
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameNONKYCCachedQuery()

    const activeMerchant = merchants.filter((val) => val.is_deleted === "0")
    const selectedMerchant = activeMerchant.find(m => m.id === merchant)

    useEffect(() => {
        // clear state before fetching: the confirm dialog must never show a previous
        // merchant's wallets or balances
        setWalletsData([]);
        setwallet('');
        setThreshold('');
        setCredit('');
        setAmount('');
        setBalance('');
        if (merchant) {
            get(`${apiConstants.WALLET}?merchantId=${merchant}`)
                .then((res) => {
                    const users = res.data.responseObj || [];
                    setWalletsData(users);
                    const active = users.find((item) => item.isActive === true);
                    if (active) {
                        setwallet(active.walletType);
                        if (active.walletType === 'Prepaid') {
                            setBalance(active.walletBalance);
                            setThreshold(active.alertThreshold);
                        } else {
                            setAmount(active.dueAmount);
                            setCredit(active.creditLimit);
                            setThreshold(active.alertThreshold);
                        }
                    }
                }).catch(() => {
                    enqueueSnackbar('Could not load this merchant\'s wallets — do not proceed with changes', { variant: 'error' });
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchant, reload])

    const activeWallet = walletsData.find((w) => w.isActive === true);
    const targetWallet = walletsData.find((w) => w.walletType === wallet);
    const isSwitch = !!(activeWallet && wallet && activeWallet.walletType !== wallet);

    const handleClickOpen = () => {
        setOpen(true);
        // pre-switch check: recharges initiated but not yet captured — a capture completing
        // AFTER the switch credits whichever wallet is active then, i.e. the wrong one.
        // States: 'loading' | 'error' | array of pending rows
        setPendingRecharges('loading');
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, {
            collection: 'recharge_payment_orders',
            limit: 100,
            andfilter: [
                { field: 'merchantOid', operator: 'eq', value: merchant },
            ],
        }).then((res) => {
            const all = res?.data?.data || [];
            const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
            // pending = not terminal (captured/paid/failed/refunded) — including legacy rows
            // with NO status field — and recent enough to still capture (old initiated
            // orders expire at the gateway and would flag forever otherwise)
            const pending = all.filter((r) => {
                const status = (r.status || '').toLowerCase();
                const terminal = ['captured', 'paid', 'failed', 'refunded', 'cancelled'].includes(status);
                const created = new Date(r.createdDate || r.lastModifiedDate || 0).getTime();
                return !terminal && created >= cutoff;
            });
            setPendingRecharges(pending);
        }).catch(() => setPendingRecharges('error'));
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleAgree = async () => {
        setIsSubmitting(true);
        try {
            // save the wallet config first, then enable it — strictly in that order
            const existingEmails = targetWallet?.alertEmails?.filter(Boolean);
            const savePayload = {
                "merchantRef": merchant,
                "walletType": wallet,
                "creditLimit": credit,
                "alertThreshold": Threshold,
            };
            // configured alert emails must be preserved on save
            if (existingEmails && existingEmails.length > 0) savePayload.alertEmails = existingEmails;

            const saveRes = await post(apiConstants.CREATE_WALLET, savePayload);
            if (saveRes?.data?.status === 'ERROR') {
                throw new Error(apiErrorMessage(saveRes.data, 'Wallet save failed'));
            }

            const enableRes = await get(`${apiConstants.ENABLE_WALLET}?merchantId=${merchant}&walletType=${wallet}`);
            if (enableRes?.data?.status === 'ERROR') {
                throw new Error(apiErrorMessage(enableRes.data, 'Wallet enable failed'));
            }

            enqueueSnackbar(`${wallet} wallet is now ACTIVE for this merchant`, { variant: 'success' });
            setReload((r) => r + 1);
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e?.response?.data, e?.message || 'Wallet switch failed'), { variant: 'error' });
        } finally {
            setIsSubmitting(false);
            handleClose();
        }
    }

    const walletCard = (w) => (
        <Card variant="outlined" key={w.id || w.walletType}
            sx={{ minWidth: 230, borderColor: w.isActive ? 'primary.main' : undefined, opacity: w.isActive ? 1 : 0.75 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">{w.walletType}</Typography>
                    <Chip size="small" label={w.isActive ? 'ACTIVE' : 'Inactive'}
                        color={w.isActive ? 'success' : 'default'} />
                </Box>
                {w.walletType === 'Prepaid' ? (
                    <Typography variant="body2">Balance: <b>{formatCurrency(w.walletBalance || 0)}</b></Typography>
                ) : (
                    <>
                        <Typography variant="body2">Credit limit: <b>{formatCurrency(w.creditLimit || 0)}</b></Typography>
                        <Typography variant="body2">Due: <b>{formatCurrency(w.dueAmount || 0)}</b></Typography>
                    </>
                )}
                <Typography variant="caption" color="text.secondary">Alert threshold: {formatCurrency(w.alertThreshold || 0)}</Typography>
            </CardContent>
        </Card>
    );

    return (

        <>
            <PageTitle>Merchant Wallet Configuration</PageTitle>
            <Grid>
                <Card>
                    <CardContent>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={1}>
                                <Grid item xs={6} md={2}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>Merchant Name
                                    <Typography component='span' color={"red"}>*</Typography></Typography></Grid>
                                <Grid item xs={6} md={3}><Typography variant='subtitle2' color={"black"}>
                                    <MerchantFilterCached />
                                </Typography></Grid>
                            </Grid>

                            {merchant && walletsData.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                                    {walletsData.map(walletCard)}
                                </Box>
                            )}

                            <>
                                <Grid container spacing={1} sx={{ paddingTop: 2 }}>
                                    <Grid item xs={6} md={2}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>Wallet Type
                                        <Typography component='span' color={"red"}>*</Typography></Typography></Grid>
                                    <Grid item xs={6} md={3}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>
                                        <Typography variant='subtitle1' color={"black"} component='span' >Prepaid</Typography>
                                        <Radio
                                            checked={wallet === 'Prepaid'}
                                            onChange={handleChangeWallet}
                                            value="Prepaid"
                                            name="radio-buttons"
                                            inputProps={{ 'aria-label': 'A' }}
                                        />
                                        {selectedMerchant?.status !== 'KYC_PENDING' && (
                                            <>
                                                <Typography variant='subtitle1' color={"black"} component='span' >Postpaid</Typography>
                                                <Radio
                                                    checked={wallet === 'Postpaid'}
                                                    onChange={handleChangeWallet}
                                                    value="Postpaid"
                                                    name="radio-buttons"
                                                    inputProps={{ 'aria-label': 'B' }}
                                                />
                                            </>
                                        )}
                                    </Typography></Grid>
                                </Grid>

                                <Grid container spacing={1} sx={{ paddingTop: 2 }}>
                                    <Grid item xs={6} md={2}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>Alert Threshold</Typography></Grid>
                                    <Grid item xs={6} md={3}><Typography variant='subtitle2' color={"black"}>
                                        <TextField id="filled-basic" type="number" sx={{ width: "100%" }} value={Threshold} onChange={handleThreshold}
                                            error={!!error}
                                            helperText={error}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon /></InputAdornment>,
                                            }} /></Typography></Grid>

                                    {wallet === "Postpaid" &&
                                        <>
                                            <Grid container spacing={1} sx={{ paddingLeft: 1, paddingTop: 2 }}>
                                                <Grid item xs={6} md={2}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>Credit Limit</Typography></Grid>
                                                <Grid item xs={6} md={3}><Typography variant='subtitle2' color={"black"}>
                                                    <TextField id="filled-basic" variant="filled" type="number" sx={{ width: "100%" }} value={credit} onChange={handleCredit}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon /></InputAdornment>,
                                                        }} /></Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={1} sx={{ paddingLeft: 1, paddingTop: 2 }}>
                                                <Grid item xs={6} md={2}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>Due Amount</Typography></Grid>
                                                <Grid item xs={6} md={3}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>
                                                    <TextField id="filled-basic" variant="filled" sx={{ width: "100%" }} value={amount} disabled
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon /></InputAdornment>,
                                                        }} />
                                                </Typography></Grid>
                                            </Grid>
                                        </>
                                    }
                                    {wallet === "Prepaid" &&
                                        <Grid container spacing={1} sx={{ paddingLeft: 1, paddingTop: 2 }}>
                                            <Grid item xs={6} md={2}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>Wallet Balance</Typography></Grid>
                                            <Grid item xs={6} md={3}><Typography variant='subtitle1' color={"black"} sx={{ paddingTop: 2 }}>
                                                {balance ? "₹ " + (balance.toLocaleString('en-US')) : "₹ " + 0}
                                            </Typography></Grid>
                                        </Grid>
                                    }

                                </Grid>


                                <Grid container spacing={1} sx={{ paddingTop: 4, display: 'flex', justifyContent: 'center' }}>
                                    <Box>
                                        <LoadingButton
                                            loading={isSubmitting}
                                            variant="contained"
                                            size="medium"
                                            type='submit' >{isSwitch ? `Switch to ${wallet}…` : 'Save'}</LoadingButton>
                                        <Dialog
                                            open={open}
                                            onClose={handleClose}
                                            aria-labelledby="alert-dialog-title"
                                            aria-describedby="alert-dialog-description"
                                        >
                                            <DialogTitle id="alert-dialog-title">
                                                {isSwitch
                                                    ? `Switch ${selectedMerchant?.name || 'merchant'}: ${activeWallet.walletType} → ${wallet}?`
                                                    : `${wallet} Wallet — save & enable`}
                                            </DialogTitle>
                                            <DialogContent>
                                                {isSwitch && (
                                                    <Box sx={{ mb: 1.5 }}>
                                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                                            The {activeWallet.walletType} wallet will be DEACTIVATED
                                                            {activeWallet.walletType === 'Prepaid'
                                                                ? ` with ${formatCurrency(activeWallet.walletBalance || 0)} remaining on it.`
                                                                : ` with ${formatCurrency(activeWallet.dueAmount || 0)} outstanding due on it.`}
                                                            {' '}That money DOES NOT move to the new wallet — it stays on the
                                                            deactivated wallet until finance settles it (recorded in Switch History
                                                            and Stranded Residuals).
                                                        </Alert>
                                                        {pendingRecharges === 'loading' && (
                                                            <Alert severity="info" sx={{ mb: 1 }}>Checking pending recharges…</Alert>
                                                        )}
                                                        {pendingRecharges === 'error' && (
                                                            <Alert severity="warning" sx={{ mb: 1 }}>
                                                                Could not check pending recharges — verify manually before switching.
                                                            </Alert>
                                                        )}
                                                        {Array.isArray(pendingRecharges) && pendingRecharges.length > 0 && (
                                                            <Alert severity="error" sx={{ mb: 1 }}>
                                                                {pendingRecharges.length} recharge(s) initiated in the last 14 days and not yet captured
                                                                (e.g. {formatCurrency(pendingRecharges[0]?.amountDue ?? pendingRecharges[0]?.amount ?? 0)}).
                                                                A capture completing AFTER the switch credits the NEW wallet. Prefer waiting for capture/expiry.
                                                            </Alert>
                                                        )}
                                                        {Array.isArray(pendingRecharges) && pendingRecharges.length === 0 && (
                                                            <Alert severity="success" sx={{ mb: 1 }}>No pending recharges in the last 14 days.</Alert>
                                                        )}
                                                        <Alert severity="info" sx={{ mb: 1 }}>
                                                            In-flight shipments of this merchant will bill their future charges
                                                            (RTO, cancellation, weight disputes) to the {wallet} wallet after the switch.
                                                        </Alert>
                                                    </Box>
                                                )}
                                                <DialogContentText id="alert-dialog-description">
                                                    {wallet} wallet will be saved with alert threshold ₹{Threshold}
                                                    {wallet === 'Postpaid' ? ` and credit limit ₹${credit}` : ''} and made ACTIVE.
                                                    Are you sure you want to proceed?
                                                </DialogContentText>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={handleClose}>Disagree</Button>
                                                <LoadingButton
                                                    loading={isSubmitting}
                                                    disabled={isSwitch && pendingRecharges === 'loading'}
                                                    color={isSwitch ? 'error' : 'primary'}
                                                    onClick={handleAgree}>
                                                    {isSwitch ? 'Switch wallet' : 'Agree'}
                                                </LoadingButton>
                                            </DialogActions>
                                        </Dialog>
                                    </Box>
                                </Grid>

                            </>
                        </FormProvider>
                    </CardContent>
                </Card>

                {merchant && (
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Switch history of this merchant</Typography>
                            <WalletSwitchHistory key={`${merchant}_${reload}`} merchantId={merchant} compact />
                        </CardContent>
                    </Card>
                )}
            </Grid>
        </>
    )
}

export default Wallet;
